import { SymbolColourControl } from './SymbolColourControl';
import { SymbolPicker } from './SymbolPicker';
import { BackingControls } from './BackingControls';
import { pointPresentation } from '../../../symbols/scene';
import { isImageGraphic } from '../../../symbols/model';
import type { VectorSourceDescriptor } from '../../../data/model';
import type { LayerDefinition } from '../../../project/model';
import { geometryFamiliesForSource } from '../../../styles/geometry';
import type { GeometrySymbolPatch, LineDashPreset } from '../../../styles/model';
import type { StyleChangeResult } from '../../state/appStore';
import { useAppStore } from '../../state/useAppStore';
import { ColourControl } from './ColourControl';
import { NumericControl } from './NumericControl';

interface SymbolControlsProps {
  readonly layer: LayerDefinition;
  readonly onResult: (result: StyleChangeResult) => void;
  readonly source: VectorSourceDescriptor;
}

export function SymbolControls({ layer, onResult, source }: SymbolControlsProps) {
  const updateSymbol = useAppStore((state) => state.updateLayerSymbol);
  const style = layer.style;
  const families = geometryFamiliesForSource(source);
  const graduatedVariable =
    style.classification.mode === 'graduated' ? style.classification.visualVariable : null;
  const pointSizeIsThematic = graduatedVariable?.kind === 'point-size';
  const lineWidthIsThematic = graduatedVariable?.kind === 'line-width';
  const updateGeometrySymbol = (patch: GeometrySymbolPatch) => {
    onResult(updateSymbol(layer.id, patch));
  };

  return (
    <>
      {families.includes('point') ? (
        <section className="inspector-section symbol-controls">
          <h3>Point symbol</h3>
          <SymbolPicker
            target={{ kind: 'point', layerId: layer.id }}
            shapes={['circle', 'square', 'triangle']}
            graphic={style.symbols.point.shape}
            presentation={pointPresentation(style.symbols.point)}
            colour={style.symbols.point.fillColor}
            {...(style.symbols.point.tint === undefined ? {} : { tint: style.symbols.point.tint })}
            onChange={(shape, anchor) =>
              updateGeometrySymbol({
                geometry: 'point',
                values: { shape, ...(anchor ? { anchor } : {}) },
              })
            }
          />
          {style.classification.mode === 'single' ? (
            <SymbolColourControl
              id={`point-fill-${layer.id}`}
              label="Marker colour"
              graphic={style.symbols.point.shape}
              colour={style.symbols.point.fillColor}
              {...(style.symbols.point.tint === undefined
                ? {}
                : { tint: style.symbols.point.tint })}
              onColour={(fillColor) =>
                updateGeometrySymbol({ geometry: 'point', values: { fillColor } })
              }
              onTint={(tint) => updateGeometrySymbol({ geometry: 'point', values: { tint } })}
            />
          ) : null}
          {!pointSizeIsThematic ? (
            <NumericControl
              id={`point-radius-${layer.id}`}
              label={isImageGraphic(style.symbols.point.shape) ? 'Size (long edge)' : 'Size'}
              max={isImageGraphic(style.symbols.point.shape) ? 80 : 40}
              min={isImageGraphic(style.symbols.point.shape) ? 2 : 1}
              onChange={(size) =>
                updateGeometrySymbol({
                  geometry: 'point',
                  values: { radius: isImageGraphic(style.symbols.point.shape) ? size / 2 : size },
                })
              }
              step={0.5}
              suffix="px"
              value={
                style.symbols.point.radius * (isImageGraphic(style.symbols.point.shape) ? 2 : 1)
              }
            />
          ) : null}
          <NumericControl
            id={`point-opacity-${layer.id}`}
            label="Opacity"
            max={100}
            min={0}
            onChange={(opacity) =>
              updateGeometrySymbol({ geometry: 'point', values: { opacity: opacity / 100 } })
            }
            step={1}
            suffix="%"
            value={Math.round(style.symbols.point.opacity * 100)}
          />
          {isImageGraphic(style.symbols.point.shape) ? (
            <>
              <NumericControl
                id={`point-rotation-${layer.id}`}
                label="Rotation"
                min={-180}
                max={180}
                step={1}
                suffix="°"
                value={style.symbols.point.rotationDegrees ?? 0}
                onChange={(rotationDegrees) =>
                  updateGeometrySymbol({ geometry: 'point', values: { rotationDegrees } })
                }
              />
              <div className="style-field style-field-inline">
                <label htmlFor={`point-anchor-${layer.id}`}>Anchor</label>
                <select
                  id={`point-anchor-${layer.id}`}
                  value={style.symbols.point.anchor ?? 'center'}
                  onChange={(e) =>
                    updateGeometrySymbol({
                      geometry: 'point',
                      values: { anchor: e.target.value as 'center' | 'bottom' },
                    })
                  }
                >
                  <option value="center">Centre</option>
                  <option value="bottom">Bottom centre</option>
                </select>
              </div>
            </>
          ) : (
            <>
              <ColourControl
                id={`point-outline-${layer.id}`}
                label="Outline colour"
                onChange={(outlineColor) =>
                  updateGeometrySymbol({ geometry: 'point', values: { outlineColor } })
                }
                value={style.symbols.point.outlineColor}
              />
              <NumericControl
                id={`point-outline-width-${layer.id}`}
                label="Outline width"
                max={12}
                min={0}
                onChange={(outlineWidth) =>
                  updateGeometrySymbol({ geometry: 'point', values: { outlineWidth } })
                }
                step={0.5}
                suffix="px"
                value={style.symbols.point.outlineWidth}
              />
            </>
          )}
          <BackingControls
            graphic={style.symbols.point.shape}
            colour={style.symbols.point.fillColor}
            tint={style.symbols.point.tint}
            size={style.symbols.point.radius * 2}
            backing={style.symbols.point.backing}
            onChange={(backing) => updateGeometrySymbol({ geometry: 'point', values: { backing } })}
          />
        </section>
      ) : null}

      {families.includes('line') ? (
        <section className="inspector-section symbol-controls">
          <h3>Line symbol</h3>
          {style.classification.mode === 'single' ? (
            <ColourControl
              id={`line-colour-${layer.id}`}
              label="Colour"
              onChange={(color) => updateGeometrySymbol({ geometry: 'line', values: { color } })}
              value={style.symbols.line.color}
            />
          ) : null}
          {!lineWidthIsThematic ? (
            <NumericControl
              id={`line-width-${layer.id}`}
              label="Width"
              max={30}
              min={0.5}
              onChange={(width) => updateGeometrySymbol({ geometry: 'line', values: { width } })}
              step={0.5}
              suffix="px"
              value={style.symbols.line.width}
            />
          ) : null}
          <NumericControl
            id={`line-opacity-${layer.id}`}
            label="Opacity"
            max={100}
            min={0}
            onChange={(opacity) =>
              updateGeometrySymbol({ geometry: 'line', values: { opacity: opacity / 100 } })
            }
            step={1}
            suffix="%"
            value={Math.round(style.symbols.line.opacity * 100)}
          />
          <div className="style-field style-field-inline">
            <label htmlFor={`line-dash-${layer.id}`}>Dash</label>
            <select
              id={`line-dash-${layer.id}`}
              onChange={(event) =>
                updateGeometrySymbol({
                  geometry: 'line',
                  values: { dash: event.target.value as LineDashPreset },
                })
              }
              value={style.symbols.line.dash}
            >
              <option value="solid">Solid</option>
              <option value="dashed">Dashed</option>
              <option value="shortDash">Short dash</option>
              <option value="dotted">Dotted</option>
              <option value="dashDot">Dash-dot</option>
            </select>
          </div>
          <NumericControl
            id={`line-offset-${layer.id}`}
            label="Offset"
            max={30}
            min={-30}
            onChange={(offset) => updateGeometrySymbol({ geometry: 'line', values: { offset } })}
            step={0.5}
            suffix="px"
            value={style.symbols.line.offset}
          />
        </section>
      ) : null}

      {families.includes('polygon') ? (
        <section className="inspector-section symbol-controls">
          <h3>Polygon symbol</h3>
          {style.classification.mode === 'single' ? (
            <ColourControl
              id={`polygon-fill-${layer.id}`}
              label="Fill colour"
              onChange={(fillColor) =>
                updateGeometrySymbol({ geometry: 'polygon', values: { fillColor } })
              }
              value={style.symbols.polygon.fillColor}
            />
          ) : null}
          <NumericControl
            id={`polygon-opacity-${layer.id}`}
            label="Fill opacity"
            max={100}
            min={0}
            onChange={(fillOpacity) =>
              updateGeometrySymbol({
                geometry: 'polygon',
                values: { fillOpacity: fillOpacity / 100 },
              })
            }
            step={1}
            suffix="%"
            value={Math.round(style.symbols.polygon.fillOpacity * 100)}
          />
          <ColourControl
            id={`polygon-outline-${layer.id}`}
            label="Outline colour"
            onChange={(outlineColor) =>
              updateGeometrySymbol({ geometry: 'polygon', values: { outlineColor } })
            }
            value={style.symbols.polygon.outlineColor}
          />
          <NumericControl
            id={`polygon-outline-width-${layer.id}`}
            label="Outline width"
            max={12}
            min={0}
            onChange={(outlineWidth) =>
              updateGeometrySymbol({ geometry: 'polygon', values: { outlineWidth } })
            }
            step={0.5}
            suffix="px"
            value={style.symbols.polygon.outlineWidth}
          />
        </section>
      ) : null}
    </>
  );
}
